<template>
  <ContentBlock>
    <div slot="instructions">
      <h1 class="f9 red">{{ $t('Coming Soon') }}</h1>
      <h5>{{ $t('Regional Pricing & Distribution') }}</h5>
      <p>
        <b class="f4">{{ $t('These features are not live yet.') }}</b>
        {{ $t('Until the launch of regional pricing all current operators will be charged the pricing set for the United States of America.') }}
      </p>
      <br />
      <!-- <p>{{ $t('After setting up your Pricing Options above, you can choose which countries you want to distribute your content to and adjust your prices per country.') }}</p> -->
    </div>
    <div>
      <div class="info">
        <div class="countries-of-distribution">
          <div class="f6">{{ $t('Countries of Distribution') }}</div>
          <div class="f3">{{ enabledPrices.length }}/{{ countries.length }}</div>
          <div class="f7">
            {{ $t('Pick which countries you want to distribute your content to in the table below.') }}
            <i18n
              v-if="contentOwnership.status === 'active'"
              class="remove-country"
              tag="span"
              path="Please contact {supportEmail} to remove countries."
            >
              <a
                class="link-style"
                slot="supportEmail"
                href="mailto:support@springboardvr.com"
              >support@springboardvr.com</a>
            </i18n>
          </div>
        </div>
        <UISwitch
          :title="$t('Enable Regionally Adjusted Pricing')"
          :rawHtml="true"
          :value="contentOwnership.regionalPricing"
          class="regional-price-switch"
          @change="toggleRegionalPricing"
        >
          <template #instructions>
            <i18n
              path="With Regionally Adjusted Pricing, locations will be charged a fair rate based on the {purchasingParityPowerLink} for their country. If you disable this, every location will be charged the same rate, making it less likely for developing countries to offer your content."
            >
              <a
                slot="purchasingParityPowerLink"
                href="https://support.springboardvr.com/hc/en-us/articles/360018577453-What-is-Purchasing-Power-Parity-"
                rel="noopener"
                target="_blank"
                class="link-style"
              >{{ $t('Purchasing Parity Power') }}</a>
            </i18n>
          </template>
        </UISwitch>
      </div>
      <div class="table">
        <div class="table-head f8 grid-layout" :class="'grid-inputs-' + numberOfInputsInGrid">
          <div>{{ $t('Distribute') }}</div>
          <div>{{ $t('Country') }}</div>
          <div class="minute-input">{{ $t('Per Minute') }}</div>
          <div
            v-if="!contentOwnership.disablePriceMonth"
            class="month-input"
          >{{ $t('Monthly') }}</div>
          <div
            class="year-input"
            v-if="contentOwnership.basePriceYear !== null"
          >{{ $t('Per Year') }}</div>
          <div
            class="play-input"
            v-if="contentOwnership.basePricePlay !== null"
          >{{ $t('Per Play') }}</div>
        </div>
        <div
          class="table-secondary-head grid-layout"
          :class="'grid-inputs-' + numberOfInputsInGrid"
        >
          <UICheckbox
            class="select-all"
            :title="$t('Select All')"
            :value="enabledPrices.length === countries.length"
            @change="updateAllDistribute(enabledPrices.length !== countries.length)"
            :disabled="contentOwnership.status === 'active' && enabledPrices.length === countries.length"
          />
          <div class="f8">{{ $t('All prices are in USD.') }}</div>
        </div>
        <div class="table-body">
          <div
            class="table-row grid-layout"
            :class="'grid-inputs-' + numberOfInputsInGrid"
            v-for="price in prices"
            :key="price.countryCode"
          >
            <UICheckbox
              class="distribute-input input"
              :value="price.distribute"
              @change="updateDistribute(price)"
              :disabled="(contentOwnership.status === 'active' && price._distributeLock) || price.country === 'USA'"
            />
            <div class="name f8">
              {{ getCountryFromcountryCode(price.country).name }}
              <!-- TODO: RE-ENABLE REGIONAL PRICING -->
              <div
                class="USA f6 red"
                v-if="price.country === 'USA'"
              >{{ $t('Until Regional Pricing is launched, this is the price that is set for every country in the marketplace.') }}</div>
              <!-- Awiting the Regional Pricing table to be fully available before making this change -->
              <!-- <sup
                v-if="price.startsAt && price.distribute"
              >{{ findIndexOfUpcomingPrice(price) }}</sup>-->
            </div>
            <UIInput
              class="minute-input input"
              type="number"
              min="0"
              max="0.99"
              :class="{ 'info-border': price.priceMinuteOverride }"
              :value="getFormattedPrice(price.priceMinute, price.distribute)"
              @input="updatePrice(price, 'priceMinute', $event.target.value, $event)"
              :disabled="!price.distribute"
            />
            <UIInput
              v-if="!contentOwnership.disablePriceMonth"
              class="month-input input"
              type="number"
              min="0"
              :class="{ 'info-border': price.priceMonthOverride }"
              :value="getFormattedPrice(price.priceMonth, price.distribute)"
              @input="updatePrice(price, 'priceMonth', $event.target.value, $event)"
              :disabled="!price.distribute"
            />
            <UIInput
              v-if="contentOwnership.basePriceYear !== null"
              class="year-input input"
              type="number"
              min="0"
              :class="{ 'info-border': price.priceYearOverride }"
              :value="getFormattedPrice(price.priceYear, price.distribute)"
              @input="updatePrice(price, 'priceYear', $event.target.value, $event)"
              :disabled="!price.distribute"
            />
            <UIInput
              v-if="contentOwnership.basePricePlay !== null"
              class="play-input input"
              type="number"
              min="0"
              max="25"
              :class="{ 'info-border': price.pricePlayOverride }"
              :value="getFormattedPrice(price.pricePlay, price.distribute)"
              @input="updatePrice(price, 'pricePlay', $event.target.value, $event)"
              :disabled="!price.distribute"
            />
          </div>
        </div>
      </div>
    </div>
    <!-- TODO: RE-ENABLE REGIONAL PRICING -->
    <!-- Awiting the Regional Pricing table to be fully available before making this change -->
    <!-- <div
      class="upcoming-price-date f7"
      v-for="(upcomingPriceDate, key, index) of upcomingPriceDates"
      :key="upcomingPriceDate"
    >
      <sup>{{ index + 1 }}</sup>
      {{ $t('Prices will be updated on') }} {{ upcomingPriceDate }}.
    </div>-->
    <div class="helper f7">
      {{ $t('Prices in ') }}
      <span class="blue">{{ $t('blue') }}</span>
      {{ $t('are the prices you manually updated.') }}
    </div>
  </ContentBlock>
</template>

<script>
import Component, { mixins } from 'vue-class-component';
import UISwitch from '@/core/shared/components/ui/UISwitch';
import UICheckbox from '@/core/shared/components/ui/UICheckbox';
import UIInput from '@/core/shared/components/ui/UIInput';
import ContentBlock from '@/core/shared/components/ContentBlock';
import RegionalPricingUpdatePriceMixin from './RegionalPricingUpdatePriceMixin';
import moment from 'moment-timezone';

@Component({
  components: {
    UISwitch,
    UICheckbox,
    UIInput,
    ContentBlock,
  },
  props: {
    contentOwnership: {
      type: Object,
      required: true,
    },
    countries: {
      type: Array,
      required: true,
    },
  },
  watch: {
    'contentOwnership.basePriceMinute': {
      handler: 'onBasePricePriceMinuteChange',
      immediate: false,
      deep: false,
    },
    'contentOwnership.basePriceMonth': {
      handler: 'onBasePricePriceMonthChange',
      immediate: false,
      deep: false,
    },
    'contentOwnership.basePriceYear': {
      handler: 'onBasePricePriceYearChange',
      immediate: false,
      deep: false,
    },
    'contentOwnership.basePricePlay': {
      handler: 'onBasePricePricePlayChange',
      immediate: false,
      deep: false,
    },
    'contentOwnership.regionalPricing': {
      handler: 'onRegionalPricingChange',
      immediate: false,
      deep: false,
    },
  },
})
export default class RegionalPricing extends mixins(RegionalPricingUpdatePriceMixin) {
  upcomingPriceDates = {};

  get localContentOwnership () {
    return new Proxy(this.contentOwnership, {
      set: (object, property, value) => {
        this.$emit('edit:contentOwnership', property, value);
        return true;
      },
    });
  }

  /**
   * These are the prices that show on the regional pricing table.
   * Basically it shows a countries' price in this order
   *
   * upcoming
   * current (if the country in question doesn't have an upcoming price show the current)
   * default (if the country isn't upcoming or current, create a default, then put that into upcoming)
   *
   * The thing is we need to make the default an 'upcoming' price at the end of this
   * operation so that when you hit save, it actually saves the data.
   *
   * So, per country we look for upcoming, then current.
   *
   * If neither exist we say 'make a default' and say it's a new upcoming price.
   * From there when we update the `upcomingPrices` array on the `contentOwnership`
   * That triggers a whole new update on this function to it runs again in the next tick.
   * now this function will find all the upcoming prices, that previously were defaults
   * The page will now be working! (hopefully)
  */
  get prices () {
    this.upcomingPriceDates = {};
    const newUpcomingPrices = [];

    const prices = this.countries.map(country => {
      // Upcoming check
      const upcomingPrice = this.localContentOwnership.upcomingPrices.find(upcomingPrice => upcomingPrice.country === country.countryCode);

      if (upcomingPrice) {
        if (upcomingPrice.startsAt) this.upcomingPriceDates[upcomingPrice.startsAt] = moment.utc(upcomingPrice.startsAt).format('LL');
        return upcomingPrice;
      }

      // Current check
      const currentPrice = this.localContentOwnership.currentPrices.find(currentPrice => currentPrice.country === country.countryCode);

      if (currentPrice) {
        const newCurrent = { ...currentPrice };
        newCurrent._distributeLock = newCurrent.distribute;
        return newCurrent;
      }

      // Set the default with regional adjustment
      let defaultPrice = {
        priceMinute: 6,
        priceMinuteOverride: false,
        priceMonth: null,
        priceMonthOverride: false,
        priceYear: null,
        priceYearOverride: false,
        pricePlay: null,
        pricePlayOverride: false,
        durationPlay: 60,
        distribute: true,
        country: country.countryCode,
      };
      if (this.localContentOwnership.regionalPricing) {
        defaultPrice = this.updatePriceWithRegionalAdjustment(defaultPrice);
      } else {
        defaultPrice = this.updatePriceWithoutRegionalAdjustment(defaultPrice);
      }
      newUpcomingPrices.push(defaultPrice);

      // send it back, just for show... it will be replaced in the next tick.
      return defaultPrice;
    });

    if (newUpcomingPrices.length > 0) {
      this.updateContentOwnershipUpcomingPrices(newUpcomingPrices);
    }

    // USA, USA, USA, MERICA
    return prices.sort((a, b) => {
      if (a.country === 'USA') return -1;
      return 0;
    });
  }

  get enabledPrices () {
    if (this.prices) {
      return this.prices.filter(price => price.distribute);
    }
  }

  get numberOfInputsInGrid () {
    let number = 2;
    if (this.localContentOwnership.basePriceYear !== null) number++;
    if (this.localContentOwnership.basePricePlay !== null) number++;
    return number;
  }

  onBasePricePriceMinuteChange () {
    if (this.contentOwnership.basePriceMinute === 'unset') {
      this.updatePrices(false);
    } else if (this.contentOwnership.minutePrice && this.contentOwnership.minutePrice.current && this.contentOwnership.minutePrice.current.price !== this.contentOwnership.basePriceMinute) {
      this.updatePrices();
    } else {
      this.updatePrices(false);
    }
  }

  onBasePricePriceMonthChange () {
    if (this.contentOwnership.basePriceMonth === null || this.contentOwnership.basePriceMonth === 'unset' || (typeof this.contentOwnership.basePriceYear === 'number' && this.contentOwnership.basePriceMonth > this.contentOwnership.basePriceYear)) {
      this.updatePrices(false);
    } else if (this.contentOwnership.monthlyPrice && this.contentOwnership.monthlyPrice.current && this.contentOwnership.monthlyPrice.current.price !== this.contentOwnership.basePriceMonth) {
      this.updatePrices();
    } else {
      this.updatePrices(false);
    }
  }

  onBasePricePriceYearChange () {
    if (this.contentOwnership.basePriceYear === null || this.contentOwnership.basePriceYear === 'unset' || this.contentOwnership.basePriceMonth > this.contentOwnership.basePriceYear) {
      this.updatePrices(false);
    } else if (this.contentOwnership.yearlyPrice && this.contentOwnership.yearlyPrice.current && this.contentOwnership.yearlyPrice.current.price !== this.contentOwnership.basePriceYear) {
      this.updatePrices();
    } else {
      this.updatePrices(false);
    }
  }

  onBasePricePricePlayChange () { this.updatePrices(false); }
  onRegionalPricingChange () { this.updatePrices(false); }

  findIndexOfUpcomingPrice (price) {
    return Object.keys(this.upcomingPriceDates).findIndex(startsAt => startsAt === price.startsAt) + 1;
  }

  getCountryFromcountryCode (countryCode) {
    return this.countries.find(country => country.countryCode === countryCode);
  }

  getFormattedPrice (price, enabled) {
    return (!enabled) ? ' —' : (price / 100).toFixed(2);
  }

  updateFlash (multiple) {
    if (this.localContentOwnership.status === 'active') {
      const date = moment.utc(this.localContentOwnership.newPriceChangeDate).format('LL');
      let message = this.$t('If you save, the price update(s) will take effect on {date} for all countries.', { date });

      // TODO: RE-ENABLE REGIONAL PRICING
      // const message = (multiple)
      //   ? this.$t('If you save, the price update will take effect on {date}.', { date })
      //   : this.$t('If you save, all the price updates for this country will take effect on {date}.', { date });

      while (message.includes('{date}')) {
        message = this.$t('If you save, the price update(s) will take effect on {date} for all countries.', { date });
      }

      setTimeout(() => {
        this.$store.commit('setFlash', {
          type: 'info',
          message,
        });
      }, 0);
    }
  }

  toggleRegionalPricing () {
    this.$emit('edit:contentOwnership', 'regionalPricing', !this.localContentOwnership.regionalPricing);
  }

  updateContentOwnershipCurrentPrices (currentPrices) {
    this.$emit('edit:contentOwnership', 'currentPrices', currentPrices);
  }

  async updateContentOwnershipUpcomingPrices (upcomingPrices) {
    this.$emit('edit:contentOwnership', 'upcomingPrices', upcomingPrices);
  }
}
</script>

<style lang="postcss" scoped>
@import "core/shared/styles";

.red {
  color: var(--colorSBRedFlat);
}

.info {
  display: grid;
  grid-gap: var(--spacingMd);
  margin-bottom: var(--spacingMd);

  @media (--tablet) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.table {
  display: grid;
  grid-gap: var(--spacingSm);
}

.table-head {
  padding-bottom: var(--spacingXS);
  border-bottom: 0.1rem solid var(--colorSlate);
}

.select-all {
  grid-column: 1 / 3;

  & >>> .title {
    color: var(--colorManatee) !important;
  }
}

.countries-of-distribution {
  & .f3 {
    margin: var(--spacingXS) 0;
  }
}

.grid-layout {
  display: grid;
  grid-template-columns: 7rem 1fr 8rem;
  grid-gap: var(--spacingXS);
  align-items: center;
  grid-template-areas: ". . minute";

  @media (--tablet) {
    grid-gap: var(--spacingSm);
  }

  & .minute-input {
    grid-area: minute;
  }
  & .month-input {
    grid-area: month;
  }
  & .year-input {
    grid-area: year;
  }
  & .play-input {
    grid-area: play;
  }

  &.grid-inputs-2 {
    grid-template-columns: 7rem 1fr 8rem;
    grid-template-areas:
      ". . minute"
      ". . month";
  }

  &.grid-inputs-3 {
    grid-template-columns: 7rem 1fr 8rem;
    grid-template-areas:
      ". . minute"
      ". . month"
      ". . year";
  }

  &.grid-inputs-4 {
    grid-template-columns: 7rem 1fr 8rem;
    grid-template-areas:
      ". . minute"
      ". . month"
      ". . year"
      ". . play";
  }

  @media (--tablet) {
    grid-template-areas: unset;

    & .minute-input {
      grid-area: auto;
    }
    & .month-input {
      grid-area: auto;
    }
    & .year-input {
      grid-area: auto;
    }
    & .play-input {
      grid-area: auto;
    }

    &.grid-inputs-2 {
      grid-template-columns: 7rem 1fr 8rem 8rem;
      grid-template-areas: unset;
    }

    &.grid-inputs-3 {
      grid-template-columns: 7rem 1fr 8rem 8rem 8rem;
      grid-template-areas: unset;
    }

    &.grid-inputs-4 {
      grid-template-columns: 7rem 1fr 8rem 8rem 8rem 8rem;
      grid-template-areas: unset;
    }
  }
}

.table-secondary-head {
  & .f8 {
    color: var(--colorManatee);
    grid-column: 3 / 5;
    text-align: left;
  }
}

.table-body {
  display: grid;
  height: 50rem;
  overflow: auto;
  -ms-overflow-style: none;
  overflow: -moz-scrollbars-none;

  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }

  & .table-row {
    border-bottom: 0.1rem solid var(--colorGeyser);
    padding: calc(var(--spacingSm) / 2) 0;

    &:last-child {
      border-bottom: none;
    }

    & .info-border >>> input {
      border: 0.1rem solid var(--colorInfoFlat);
    }

    & >>> input {
      padding: var(--spacingXS);
    }
  }
}

.upcoming-price-date {
  margin-top: var(--spacingSm);
}

.blue {
  display: inline-block;
  margin-top: var(--spacingSm);
  color: var(--colorInfoFlat);
}
</style>
